import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import {
  acquitterAlertes, chargerAlertes, chargerRegles, chargerReglages, chargerZones, etatAlerte, FILTRE_PAR_DEFAUT, filtrerAlertes, LIBELLE_SEVERITE, LIBELLE_TYPE_ALERTE,
  libelleCanal, lienCarte, PASTILLE_SEVERITE, type AlerteGps, type FiltreAlertes, type Regle, type ReglagesAlertes, type StatutAlerte, type TypeAlerte, type Zone,
} from '../../api/alertes'
import { nomBoitier } from '../../api/gps'
import type { DonneesOps } from '../../api/types'
import { useDroits } from '../Contexte'
import { dateCourte, heure } from '../Format'
import { Icone, type NomIcone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { ecrire } from './AlertesOutils'
import { OngletNotifications } from './AlertesNotifications'
import { OngletRegles } from './AlertesRegles'
import { OngletZones } from './AlertesZones'

/**
 * « Alertes & zones » (migration 0052) — règles d'alerte de l'organisation opératrice, zones dessinées sur la carte, historique.
 *
 * Les règles sont évaluées PAR LA BASE à l'arrivée de chaque position : cet écran ne fait que les régler et en afficher le
 * résultat. Tout membre d'équipe lit (`is_org_staff`) ; propriétaire et dispatcher gèrent (`has_org_role`), le comptable consulte.
 * L'organisation opératrice est exemptée de palier : rien à souscrire. Une alerte = une infraction : elle se rafraîchit au lieu
 * de se répéter, et se referme toute seule quand elle cesse.
 */
type Onglet = 'alertes' | 'regles' | 'zones' | 'notifications'

const message = (e: unknown) => (e instanceof Error ? e.message : String(e))

export function EcranAlertes({
  donnees,
  orgId,
  surNotifier,
  surAcquittement,
}: {
  donnees: DonneesOps
  orgId: string
  surNotifier: (message: string) => void
  /** Après un acquittement : la pastille de la navigation se remet à jour sans attendre les 30 s. */
  surAcquittement: () => void
}) {
  const droits = useDroits()
  const { apercu, fixturesAlertes } = droits
  const gere = droits.peut('alertes_gerer')
  const [onglet, setOnglet] = useState<Onglet>('alertes')
  const [regles, setRegles] = useState<Regle[]>(fixturesAlertes?.regles ?? [])
  const [zones, setZones] = useState<Zone[]>(fixturesAlertes?.zones ?? [])
  const [reglages, setReglages] = useState<ReglagesAlertes>(fixturesAlertes?.reglages ?? { emails: [], telegramChatId: null, telegramVerifie: false })
  const [chargement, setChargement] = useState(!fixturesAlertes)
  const [indisponible, setIndisponible] = useState(false)

  const noms = useMemo(() => new Map(donnees.boitiers.map((b) => [b.id, nomBoitier(b, donnees.vehicules)])), [donnees.boitiers, donnees.vehicules])

  const recharger = useCallback(async () => {
    if (fixturesAlertes) return
    try {
      const [r, z, g] = await Promise.all([chargerRegles(orgId), chargerZones(orgId), chargerReglages(orgId)])
      setRegles(r)
      setZones(z)
      setReglages(g)
      setIndisponible(false)
    } catch (e) {
      /* Migration 0052 pas encore appliquée : la table n'existe pas — on l'explique plutôt que d'afficher une erreur SQL. */
      if (/does not exist|schema cache|could not find the (table|column|function)|42P01|PGRST205/i.test(message(e))) setIndisponible(true)
      else surNotifier(message(e))
    } finally {
      setChargement(false)
    }
  }, [orgId, fixturesAlertes, surNotifier])

  useEffect(() => {
    void recharger()
  }, [recharger])

  if (indisponible) {
    return (
      <div className="bandeau alerte">
        Les alertes GPS ne sont pas encore activées sur la base (la migration <span className="mono">0052</span> n’est pas appliquée) : les règles et les zones
        ne peuvent pas encore être enregistrées.
      </div>
    )
  }

  const ONGLETS: ReadonlyArray<{ id: Onglet; libelle: string; icone: NomIcone }> = [
    { id: 'alertes', libelle: 'Alertes', icone: 'bell' },
    { id: 'regles', libelle: `Règles · ${regles.filter((r) => r.actif).length}`, icone: 'gear' },
    { id: 'zones', libelle: `Zones · ${zones.filter((z) => z.actif).length}`, icone: 'pin' },
    { id: 'notifications', libelle: 'Notifications', icone: 'send' },
  ]

  return (
    <>
      <div className="subtabs" role="tablist" aria-label="Alertes et zones">
        {ONGLETS.map((o) => (
          <button key={o.id} role="tab" aria-selected={o.id === onglet} className={`subtab${o.id === onglet ? ' active' : ''}`} onClick={() => setOnglet(o.id)}>
            <Icone nom={o.icone} /> {o.libelle}
          </button>
        ))}
      </div>
      {!gere && (
        <p className="t-muted" style={{ fontSize: 13, marginBottom: 12 }}>
          Votre rôle permet de consulter les alertes ; seuls le propriétaire et le dispatch modifient les règles, les zones et les notifications.
        </p>
      )}
      {donnees.boitiers.length === 0 && !apercu && (
        <div className="bandeau">Aucun boîtier GPS pour l’instant : les alertes se déclenchent à partir des positions reçues des boîtiers (écran « Boîtiers GPS »).</div>
      )}
      {onglet === 'alertes' && <OngletAlertes noms={noms} orgId={orgId} gere={gere} surNotifier={surNotifier} surAcquittement={surAcquittement} />}
      {onglet === 'regles' && (
        <OngletRegles regles={regles} reglages={reglages} donnees={donnees} orgId={orgId} gere={gere} chargement={chargement} surNotifier={surNotifier} surChangement={recharger} />
      )}
      {onglet === 'zones' && <OngletZones zones={zones} donnees={donnees} orgId={orgId} gere={gere} surNotifier={surNotifier} surChangement={recharger} />}
      {onglet === 'notifications' && <OngletNotifications reglages={reglages} orgId={orgId} gere={gere} surNotifier={surNotifier} surChangement={recharger} />}
    </>
  )
}

/* ------------------------------------------------------------------ historique */

const STATUTS: readonly { id: StatutAlerte; libelle: string }[] = [
  { id: 'a_traiter', libelle: 'À traiter' },
  { id: 'ouvertes', libelle: 'En cours' },
  { id: 'terminees', libelle: 'Terminées' },
  { id: 'toutes', libelle: 'Toutes' },
]

function OngletAlertes({
  noms,
  orgId,
  gere,
  surNotifier,
  surAcquittement,
}: {
  noms: ReadonlyMap<string, string>
  orgId: string
  gere: boolean
  surNotifier: (message: string) => void
  surAcquittement: () => void
}) {
  const { apercu, fixturesAlertes } = useDroits()
  const [filtre, setFiltre] = useState<FiltreAlertes>(FILTRE_PAR_DEFAUT)
  const [liste, setListe] = useState<AlerteGps[]>(() => (fixturesAlertes ? filtrerAlertes(fixturesAlertes.alertes, FILTRE_PAR_DEFAUT) : []))
  const [chargement, setChargement] = useState(!fixturesAlertes)
  const [ouverte, setOuverte] = useState<number | null>(null)
  const [maintenant, setMaintenant] = useState(() => Date.now())

  const charger = useCallback(async () => {
    if (fixturesAlertes) {
      setListe(filtrerAlertes(fixturesAlertes.alertes, filtre))
      return
    }
    try {
      setListe(await chargerAlertes(orgId, filtre))
    } catch (e) {
      surNotifier(message(e))
    } finally {
      setChargement(false)
    }
  }, [orgId, filtre, fixturesAlertes, surNotifier])

  useEffect(() => {
    setChargement(!fixturesAlertes)
    void charger()
    /* L'historique se rafraîchit tout seul : une alerte arrive sans que personne n'ait rien touché. */
    const t = setInterval(() => {
      setMaintenant(Date.now())
      void charger()
    }, 30_000)
    return () => clearInterval(t)
  }, [charger, fixturesAlertes])

  const aTraiter = liste.filter((a) => a.acquitteeLe === null)

  async function acquitter(ids: number[] | null) {
    const ok = await ecrire(
      { apercu, surNotifier },
      () => acquitterAlertes(orgId, ids),
      ids === null || ids.length > 1 ? 'Alertes acquittées' : 'Alerte acquittée',
    )
    if (!ok) return
    surAcquittement()
    await charger()
  }

  return (
    <div className="card" data-anim>
      <div className="card-head">
        <div>
          <h3>
            <Icone nom="bell" classe="h-ic" /> Historique des alertes
          </h3>
          <div className="ch-sub">
            Une infraction = une alerte : elle se rafraîchit au lieu de se répéter et se referme quand la condition disparaît. « Acquitter » = « j’ai vu » (une
            alarme ponctuelle est alors close).
          </div>
        </div>
        <span className="sp" />
        {gere && aTraiter.length > 0 && (
          <button className="btn btn-gold btn-sm" onClick={() => void acquitter(null)}>
            <Icone nom="check" /> Tout acquitter
          </button>
        )}
      </div>
      <div className="card-body">
        <div className="filtres-alertes">
          <div className="subtabs" role="tablist" aria-label="État des alertes">
            {STATUTS.map((s) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={s.id === filtre.statut}
                className={`subtab${s.id === filtre.statut ? ' active' : ''}`}
                onClick={() => setFiltre({ ...filtre, statut: s.id })}
              >
                {s.libelle}
              </button>
            ))}
          </div>
          <ListeDeroulante
            valeur={filtre.type}
            surChangement={(v) => setFiltre({ ...filtre, type: v as TypeAlerte | '' })}
            options={[{ valeur: '', libelle: 'Tous les types' }, ...(Object.keys(LIBELLE_TYPE_ALERTE) as TypeAlerte[]).map((t) => ({ valeur: t, libelle: LIBELLE_TYPE_ALERTE[t] }))]}
          />
          <ListeDeroulante
            valeur={filtre.severite}
            surChangement={(v) => setFiltre({ ...filtre, severite: v as FiltreAlertes['severite'] })}
            options={[
              { valeur: '', libelle: 'Toutes les gravités' },
              { valeur: 'urgent', libelle: 'Urgentes' },
              { valeur: 'attention', libelle: 'Attention' },
              { valeur: 'info', libelle: 'Informations' },
            ]}
          />
          <ListeDeroulante
            valeur={filtre.deviceId}
            surChangement={(v) => setFiltre({ ...filtre, deviceId: v })}
            options={[{ valeur: '', libelle: 'Tous les boîtiers' }, ...[...noms.entries()].map(([id, nom]) => ({ valeur: id, libelle: nom }))]}
          />
          <ListeDeroulante
            valeur={String(filtre.jours)}
            surChangement={(v) => setFiltre({ ...filtre, jours: Number(v) })}
            options={[
              { valeur: '1', libelle: '24 dernières heures' },
              { valeur: '7', libelle: '7 derniers jours' },
              { valeur: '30', libelle: '30 derniers jours' },
              { valeur: '0', libelle: 'Tout (90 jours conservés)' },
            ]}
          />
        </div>
      </div>
      <div className="card-body flush">
        {chargement ? (
          <div className="no-hit" style={{ padding: 18 }}>Chargement…</div>
        ) : liste.length === 0 ? (
          <div className="no-hit" style={{ padding: 18 }}>
            <b>{filtre.statut === 'a_traiter' ? 'Aucune alerte à traiter.' : 'Aucune alerte.'}</b>
            <br />
            {filtre.statut === 'a_traiter' ? 'Tout va bien : rien ne demande votre attention.' : 'Aucune alerte ne correspond à ces filtres.'}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Alerte</th>
                  <th>Boîtier</th>
                  <th>Début</th>
                  <th>Gravité</th>
                  <th>État</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {liste.map((a) => {
                  const e = etatAlerte(a)
                  const carte = lienCarte(a.detail)
                  const developpee = ouverte === a.id
                  return (
                    <Fragment key={a.id}>
                      <tr className={a.acquitteeLe === null && a.resolueLe === null ? 'ligne-alerte neuve' : 'ligne-alerte'}>
                        <td className="titre-alerte">
                          <div className="t-strong">{a.titre}</div>
                          <div className="t-muted">
                            {LIBELLE_TYPE_ALERTE[a.type]}
                            {a.nbOccurrences > 1 ? ` · répétée ${a.nbOccurrences} fois` : ''}
                          </div>
                        </td>
                        <td>{noms.get(a.deviceId) ?? a.boitierNom}</td>
                        <td>
                          {dateCourte(a.debutLe)} {heure(a.debutLe)}
                          {a.resolueLe && <div className="t-muted">fin {heure(a.resolueLe)}</div>}
                        </td>
                        <td>
                          <span className={`badge ${PASTILLE_SEVERITE[a.severite]}`}>{LIBELLE_SEVERITE[a.severite]}</span>
                        </td>
                        <td>
                          <span className={`badge ${e.pastille}`}>{e.libelle}</span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setOuverte(developpee ? null : a.id)} aria-expanded={developpee}>
                            Détail
                          </button>{' '}
                          {gere && a.acquitteeLe === null && (
                            <button className="btn btn-ghost" onClick={() => void acquitter([a.id])}>
                              Acquitter
                            </button>
                          )}
                        </td>
                      </tr>
                      {developpee && (
                        <tr className="detail-alerte">
                          <td colSpan={6}>
                            <DetailAlerte a={a} carte={carte} maintenant={maintenant} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function DetailAlerte({ a, carte, maintenant }: { a: AlerteGps; carte: string | null; maintenant: number }) {
  const d = a.detail
  const lignes: string[] = []
  if (typeof d.vitesse_max_kmh === 'number') lignes.push(`Vitesse maximale ${Math.round(d.vitesse_max_kmh)} km/h, limite ${d.limite_kmh ?? '?'} km/h.`)
  if (typeof d.duree_s === 'number') lignes.push(`Dépassement depuis ${Math.round(d.duree_s)} s.`)
  if (typeof d.duree_min === 'number') lignes.push(`Durée : ${d.duree_min} min.`)
  if (d.zone) lignes.push(`Zone : ${String(d.zone)}.`)
  if (typeof d.decel_kmh_s === 'number') lignes.push(`Décélération estimée : ${d.decel_kmh_s} km/h par seconde.`)
  if (d.calcule === true) lignes.push('Valeur ESTIMÉE à partir de deux positions consécutives : le boîtier ne mesure pas l’accélération.')
  const canaux = [libelleCanal('E-mail', a.email), libelleCanal('Telegram', a.telegram)].filter(Boolean)
  return (
    <div className="detail-alerte-c">
      {lignes.map((l) => (
        <p key={l}>{l}</p>
      ))}
      <p className="t-muted">
        Dernière occurrence : {dateCourte(a.derniereOccurrenceLe)} {heure(a.derniereOccurrenceLe)}
        {a.resolueLe === null && Date.parse(a.derniereOccurrenceLe) < maintenant - 3_600_000 ? ' (alerte ouverte depuis plus d’une heure)' : ''}.
      </p>
      {canaux.length > 0 ? (
        <p className="t-muted">{canaux.join(' · ')}</p>
      ) : (
        <p className="t-muted">Notification : historique seulement (e-mail et Telegram non demandés par la règle).</p>
      )}
      {carte && (
        <p>
          <a href={carte} target="_blank" rel="noopener noreferrer">
            Voir l’endroit sur la carte
          </a>
        </p>
      )}
    </div>
  )
}
